import { Button, Input, message, Spin, Popconfirm } from "antd";
import { CheckCircleFilled, DeleteOutlined, SyncOutlined, PlusOutlined, EditOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connectOAuthAccount, getOAuthAccounts, updateOAuthAccount, deleteOAuthAccount, syncFanpages, getFacebookLoginUrl } from "../../api/OAuthAccount";
import type { OAuthAccountItem } from "../../api/OAuthAccount";

export default function ConnectFacebookView() {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const [accessToken, setAccessToken] = useState("");
    const [appSecret, setAppSecret] = useState("");
    const [appId, setAppId] = useState("");
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [accounts, setAccounts] = useState<OAuthAccountItem[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [editingAccount, setEditingAccount] = useState<OAuthAccountItem | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [syncingId, setSyncingId] = useState<number | null>(null);
    const [authorizingId, setAuthorizingId] = useState<number | null>(null);

    useEffect(() => {
        fetchFacebookAccounts();
    }, []);


    const fetchFacebookAccounts = async () => {
        setFetching(true);
        try {
            // Phải lọc, nếu không danh sách trả về gồm cả zalo/tiktok/instagram/import_excel.
            // Dùng `search` (khớp gần đúng theo provider) thay vì `provider` (khớp tuyệt đối,
            // sẽ bỏ sót các tài khoản đặt tên kiểu "facebook khothi", "Facebook app").
            const res = await getOAuthAccounts({ search: "facebook" });
            if (res.status && res.data?.items) {
                setAccounts(
                    res.data.items.filter((a) =>
                        String(a.provider || "").toLowerCase().includes("facebook")
                    )
                );
            }
        } catch {
            // ignore
        } finally {
            setFetching(false);
        }
    };

    const handleConnect = async () => {
        setLoading(true);
        try {
            let res;
            if (editingAccount) {
                res = await updateOAuthAccount(
                    editingAccount.id,
                    "facebook",
                    accessToken || undefined,
                    appSecret || undefined,
                    appId || undefined,
                    name || undefined
                );
            } else {
                res = await connectOAuthAccount(
                    "facebook",
                    accessToken || undefined,
                    appSecret || undefined,
                    appId || undefined,
                    name || undefined
                );
            }
            if (res.status) {
                message.success(editingAccount ? t('chats.update_success') : t('chats.connect_success'));
                setShowForm(false);
                setEditingAccount(null);
                setName("");
                setAccessToken("");
                setAppSecret("");
                setAppId("");
                fetchFacebookAccounts();
            } else {
                message.error(res.msg || t('chats.connect_failed'));
            }
        } catch {
            message.error(t('chats.connect_failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (account: OAuthAccountItem) => {
        setDeletingId(account.id);
        try {
            const res = await deleteOAuthAccount(account.id);
            if (res.status) {
                message.success(t('chats.delete_success'));
                fetchFacebookAccounts();
            } else {
                message.error(res.msg || t('chats.delete_failed'));
            }
        } catch {
            message.error(t('chats.delete_failed'));
        } finally {
            setDeletingId(null);
        }
    };

    const handleSyncFanpages = async (account: OAuthAccountItem) => {
        setSyncingId(account.id);
        try {
            const res = await syncFanpages(account.id);
            if (res.status) {
                message.success(t('chats.sync_fanpage_success'));
            } else {
                message.error(res.msg || t('chats.sync_fanpage_failed'));
            }
        } catch {
            message.error(t('chats.sync_fanpage_failed'));
        } finally {
            setSyncingId(null);
        }
    };

    // Chuyển hướng cả tab sang trang cấp quyền của Facebook.
    // Sau khi xong, backend redirect về /facebook/callback-status để hiển thị kết quả.
    const handleAuthorize = async (account: OAuthAccountItem) => {
        setAuthorizingId(account.id);
        const res = await getFacebookLoginUrl(account.id);
        if (!res.status || !res.authUrl) {
            setAuthorizingId(null);
            message.error(res.msg || t('chats.fb_authorize_failed'));
            return;
        }
        window.location.href = res.authUrl;
    };

    const handleEdit = (account: OAuthAccountItem) => {
        setEditingAccount(account);
        setName(account.name || "");
        setAccessToken(account.access_token || "");
        setAppSecret(account.app_secret || "");
        setAppId(account.app_id ? String(account.app_id) : "");
        setShowForm(true);
    };

    const handleAddNew = () => {
        setEditingAccount(null);
        setName("");
        setAccessToken("");
        setAppSecret("");
        setAppId("");
        setShowForm(true);
    };

    const handleCancelForm = () => {
        setShowForm(false);
        setEditingAccount(null);
        setName("");
        setAccessToken("");
        setAppSecret("");
        setAppId("");
    };

    if (fetching) {
        return (
            <div className="md:pl-6 flex items-center justify-center h-full">
                <Spin size="large" />
            </div>
        );
    }

    // min-h-0 + overflow-hidden: grid/flex item mặc định có min-height:auto nên vẫn nở
    // theo nội dung và tràn khỏi modal dù khung cha đã cố định chiều cao
    return (
        <div className="md:pl-6 flex flex-col h-full min-h-0 md:overflow-hidden">
            {/* HEADER */}
            <div className="flex justify-between items-center mb-4">
                <div className="text-base font-medium">
                    {t('chats.add_facebook')}
                </div>
                {!showForm && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddNew}
                        className="bg-[#3e91ab] hover:!bg-[#357f96]"
                    >
                        {t('chats.add_connection')}
                    </Button>
                )}
            </div>

            {/* ADD / EDIT FORM */}
            {showForm && (
                <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
                    <div className="flex flex-col items-center text-center">
                        {/* ICONS */}
                        <div className="flex items-center gap-6 mb-6">
                            <div className="w-[48px] h-[48px] bg-white rounded-full shadow-md flex items-center justify-center">
                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xl">
                                    <img src="https://khothi.buso.asia/uploads/2025/10/photo_library/i0sGPLyOtv3a2fb45cb6264d9f6848b54d45c8a57f.png" alt="Buso" className="w-full h-full object-contain" />
                                </div>
                            </div>
                            <div className="icon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#3e91ab" viewBox="0 0 256 256"><path d="M24,128A72.08,72.08,0,0,1,96,56H204.69L194.34,45.66a8,8,0,0,1,11.32-11.32l24,24a8,8,0,0,1,0,11.32l-24,24a8,8,0,0,1-11.32-11.32L204.69,72H96a56.06,56.06,0,0,0-56,56,8,8,0,0,1-16,0Zm200-8a8,8,0,0,0-8,8,56.06,56.06,0,0,1-56,56H51.31l10.35-10.34a8,8,0,0,0-11.32-11.32l-24,24a8,8,0,0,0,0,11.32l24,24a8,8,0,0,0,11.32-11.32L51.31,200H160a72.08,72.08,0,0,0,72-72A8,8,0,0,0,224,120Z"></path></svg></div>
                            <div className="w-[48px] h-[48px] !rounded-full flex items-center justify-center text-white text-3xl shadow-md overflow-hidden">
                                <img src="https://pancake.vn/static/images/platform-connection/facebook.png" alt="Facebook" className="w-full h-full object-contain" />
                            </div>
                        </div>

                        <h2 className="text-xl font-semibold text-gray-800 mb-3">
                            {editingAccount ? t('chats.edit_buso_facebook') : t('chats.connect_buso_facebook')}
                        </h2>
                        <p className="text-gray-500 max-w-[340px] text-sm mb-6 leading-relaxed">
                            {t('chats.facebook_desc')}
                        </p>

                        {/* FORM FIELDS
                            Chrome coi cặp "ô text + ô password" là form đăng nhập và tự điền
                            email/mật khẩu đã lưu vào Access Token / App Secret. Chặn bằng cách:
                              - đặt name không giống username/password để không dính heuristic
                              - autoComplete="off" cho ô thường, "new-password" cho ô mật khẩu
                              - ô mồi ẩn để trình duyệt "đổ" gợi ý vào đó thay vì ô thật */}
                        <div className="w-[340px] flex flex-col gap-3 mb-6 text-left">
                            <input
                                type="text"
                                name="fb-autofill-decoy"
                                autoComplete="username"
                                tabIndex={-1}
                                aria-hidden="true"
                                className="hidden"
                            />
                            <div>
                                <label className="text-sm text-gray-600 mb-1 block">{t('chats.name')}</label>
                                <Input
                                    name="fb-name"
                                    autoComplete="off"
                                    placeholder={t('chats.input_name')}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-600 mb-1 block">App ID</label>
                                <Input
                                    name="fb-app-id"
                                    autoComplete="off"
                                    placeholder={t('chats.input_app_id')}
                                    value={appId}
                                    onChange={(e) => setAppId(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-600 mb-1 block">Access Token</label>
                                <Input
                                    name="fb-access-token"
                                    autoComplete="off"
                                    placeholder={t('chats.input_access_token')}
                                    value={accessToken}
                                    onChange={(e) => setAccessToken(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-600 mb-1 block">App Secret</label>
                                <Input.Password
                                    name="fb-app-secret"
                                    autoComplete="new-password"
                                    placeholder={t('chats.input_app_secret')}
                                    value={appSecret}
                                    onChange={(e) => setAppSecret(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* BUTTONS */}
                        <div className="flex gap-2">
                            <Button
                                size="large"
                                onClick={handleCancelForm}
                                className="h-[42px]"
                            >
                                {t('chats.cancel')}
                            </Button>
                            <Button
                                size="large"
                                loading={loading}
                                onClick={handleConnect}
                                className="
                                    w-[240px]
                                    h-[42px]
                                    flex items-center justify-center gap-2
                                    border border-gray-300
                                    hover:!border-[#3e91ab]
                                    bg-[#3e91ab]
                                    hover:!bg-[#357f96]
                                    hover:border-[#357f96]
                                    text-white
                                    hover:!text-white
                                "
                            >
                                <div className="w-[20px] h-[20px]">
                                    <img className="platform-icon-img facebook rounded-[4px]" src="https://pancake.vn/static/images/platform-connection/facebook.png" alt="facebook" />
                                </div>
                                <span className="font-medium">
                                    {editingAccount ? t('chats.update_facebook') : t('chats.reconnect_facebook')}
                                </span>
                            </Button>
                        </div>

                        <div className="mt-4 text-sm text-[#357f96] cursor-pointer hover:underline">
                            {t('chats.guide')}
                        </div>
                    </div>
                </div>
            )}

            {/* ACCOUNT LIST — cuộn trong khung, tránh danh sách dài tràn ra ngoài modal */}
            {accounts.length > 0 ? (
                <div className="flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto pr-1">
                    {accounts.map((account) => (
                        <div
                            key={account.id}
                            className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-[40px] h-[40px] rounded-full overflow-hidden shadow-sm flex-shrink-0">
                                    <img src="https://pancake.vn/static/images/platform-connection/facebook.png" alt="Facebook" className="w-full h-full object-contain" />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-medium text-gray-800 truncate">
                                        {account.name || account.provider || `Facebook #${account.id}`}
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        ID: {account.id}
                                    </div>
                                    <div className="text-xs text-green-600 flex items-center gap-1">
                                        <CheckCircleFilled /> {t('chats.connected')}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    icon={<SafetyCertificateOutlined />}
                                    loading={authorizingId === account.id}
                                    onClick={() => handleAuthorize(account)}
                                >
                                    {t('chats.fb_authorize')}
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<SyncOutlined spin={syncingId === account.id} />}
                                    loading={syncingId === account.id}
                                    onClick={() => handleSyncFanpages(account)}
                                    className="bg-[#3e91ab] hover:!bg-[#357f96]"
                                >
                                    {t('chats.sync_fanpage')}
                                </Button>
                                <Button
                                    type="text"
                                    icon={<EditOutlined />}
                                    size="small"
                                    onClick={() => handleEdit(account)}
                                />
                                <Popconfirm
                                    title={t('chats.delete_confirm')}
                                    onConfirm={() => handleDelete(account)}
                                    okText={t('chats.confirm_yes')}
                                    cancelText={t('chats.cancel')}
                                    okButtonProps={{ danger: true, loading: deletingId === account.id }}
                                >
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        size="small"
                                    />
                                </Popconfirm>
                            </div>
                        </div>
                    ))}
                </div>
            ) : !showForm ? (
                <div className="flex flex-col items-center justify-center text-center py-12">
                    {/* ICONS */}
                    <div className="flex items-center gap-6 mb-6">
                        <div className="w-[48px] h-[48px] bg-white rounded-full shadow-md flex items-center justify-center">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xl">
                                <img src="https://khothi.buso.asia/uploads/2025/10/photo_library/i0sGPLyOtv3a2fb45cb6264d9f6848b54d45c8a57f.png" alt="Buso" className="w-full h-full object-contain" />
                            </div>
                        </div>
                        <div className="icon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#3e91ab" viewBox="0 0 256 256"><path d="M24,128A72.08,72.08,0,0,1,96,56H204.69L194.34,45.66a8,8,0,0,1,11.32-11.32l24,24a8,8,0,0,1,0,11.32l-24,24a8,8,0,0,1-11.32-11.32L204.69,72H96a56.06,56.06,0,0,0-56,56,8,8,0,0,1-16,0Zm200-8a8,8,0,0,0-8,8,56.06,56.06,0,0,1-56,56H51.31l10.35-10.34a8,8,0,0,0-11.32-11.32l-24,24a8,8,0,0,0,0,11.32l24,24a8,8,0,0,0,11.32-11.32L51.31,200H160a72.08,72.08,0,0,0,72-72A8,8,0,0,0,224,120Z"></path></svg></div>
                        <div className="w-[48px] h-[48px] !rounded-full flex items-center justify-center text-white text-3xl shadow-md overflow-hidden">
                            <img src="https://pancake.vn/static/images/platform-connection/facebook.png" alt="Facebook" className="w-full h-full object-contain" />
                        </div>
                    </div>

                    <h2 className="text-xl font-semibold text-gray-800 mb-3">
                        {t('chats.connect_buso_facebook')}
                    </h2>
                    <p className="text-gray-500 max-w-[340px] text-sm mb-6 leading-relaxed">
                        {t('chats.facebook_desc')}
                    </p>
                    <Button
                        size="large"
                        onClick={handleAddNew}
                        className="
                            w-[340px]
                            h-[42px]
                            flex items-center justify-center gap-2
                            border border-gray-300
                            hover:!border-[#3e91ab]
                            bg-[#3e91ab]
                            hover:!bg-[#357f96]
                            hover:border-[#357f96]
                            text-white
                            hover:!text-white
                        "
                    >
                        <div className="w-[20px] h-[20px]">
                            <img className="platform-icon-img facebook rounded-[4px]" src="https://pancake.vn/static/images/platform-connection/facebook.png" alt="facebook" />
                        </div>
                        <span className="font-medium">
                            {t('chats.reconnect_facebook')}
                        </span>
                    </Button>
                    <div className="mt-4 text-sm text-[#357f96] cursor-pointer hover:underline">
                        {t('chats.guide')}
                    </div>
                </div>
            ) : null}
        </div>
    );
}